import { useState, useEffect } from "react";
import { useNotification } from "../../../context/NotificationContext";
import AdminLayout from "../../../components/admin/AdminLayout";
import { useProductsData } from "../../../hooks/useProductsData";
import AdminCategoryService from "../../../services/admin/AdminCategoryServices";
import AdminProductService from "../../../services/admin/AdminProductService";
import { useProductFilters } from "../../../hooks/useProductFilters";
import { useProductSelection } from "../../../hooks/useProductSelection";
import { useProductForm } from "../../../hooks/useProductForm";
import { ProductsHeader } from "../../../components/admin/ProductsHeader";
import { ProductsFilters } from "../../../components/admin/ProductsFilters";
import { BulkActionsBar } from "../../../components/admin/BulkActionsBar";
import { LoadingSkeleton } from "../../../components/admin/CategoriesPage/LoadingSkeleton";
import { ProductsTable } from "../../../components/admin/ProductsTable";
import { EmptyState } from "../../../components/admin/EmptyState";
import { ViewProductModal } from "../../../components/admin/ViewProductModal";
import AddEditProductModal from "../../../components/admin/AddEditProductModal";
import { useAdminPermissions } from "../../../hooks/useAdminPermissions";





export default function ProductsPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { showNotification } = useNotification();
  const { checkPermission } = useAdminPermissions();

  const [categories, setCategories] = useState([]);

  // Custom hooks
  const {
    products,
    loading: productsLoading,
    loadingStatusMap,
    loadingPopularMap,
    fetchProducts,
    handleToggleStatus,
    handleTogglePopular,
    handleDeleteProduct,
    handleBulkAction,
    addProducts,
    setProducts
  } = useProductsData();

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);

      try {
        const [_, categoriesData] = await Promise.all([
          fetchProducts(),
          AdminCategoryService.list(),
        ]);

        if (isMounted) {
          setCategories(categoriesData || []);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);


  const handleSaveProduct = async ({ mode, productId, formData }) => {
    if (!checkPermission("admin", `${mode === "edit" ? "update" : "create"} products`)) return;

    showNotification({
      type: "info",
      title: "Saving Product",
      message: `${mode === "edit" ? "Updating" : "Creating"} product, please wait...`,
    });

    try {
      let res;

      if (mode === "edit") {
        res = await AdminProductService.update(productId, formData);
        const updatedProduct = res.data?.data;

        if (!updatedProduct || !updatedProduct.id) {
          throw new Error("Invalid update response shape");
        }

        setProducts((prev) =>
          prev.map((p) => (p.id === productId ? updatedProduct : p))
        );

        showNotification({
          type: "success",
          title: "Product Updated",
          message: `"${updatedProduct.name}" has been updated successfully.`,
        });
      } else {
        res = await AdminProductService.create(formData);
        const { data: createdProducts, errors } = res.data;

        if (createdProducts && createdProducts.length > 0) {
          addProducts(createdProducts);
          showNotification({
            type: "success",
            title: "Product Created",
            message: createdProducts.length > 1
              ? `Successfully created ${createdProducts.length} products.`
              : `"${createdProducts[0]?.name || "Product"}" has been created successfully.`,
          });
        }

        if (errors && errors.length > 0) {
          errors.forEach((err) => {
            showNotification({
              type: "error",
              title: `Item ${err.index + 1} Error`,
              message: err.message,
            });
          });
        }
      }

      setShowAddModal(false);
      setEditingProduct(null);
    } catch (err) {
      console.error("Product save failed", err);

      const errorData = err?.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        errorData.errors.forEach((e) => {
          showNotification({
            type: "error",
            title: `Item ${e.index + 1} Error`,
            message: e.message,
          });
        });
      } else {
        showNotification({
          type: "error",
          title: "Save Failed",
          message: errorData?.message || "Failed to save product",
        });
      }
    }
  };



  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    categoryFilter,
    setCategoryFilter,
    popularFilter,
    setPopularFilter,
    sortBy,
    setSortBy,
    filteredProducts,
  } = useProductFilters(products);

  const {
    selectedProducts,
    handleSelectAll,
    handleSelectProduct,
    clearSelection,
  } = useProductSelection();




  const handleViewProduct = (product) => {
    setViewingProduct(product);
    setShowViewModal(true);
  };





  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowAddModal(true);
  };

  const onBulkAction = (action) => {
    if (!checkPermission("admin", "perform bulk actions")) return;
    handleBulkAction(action, selectedProducts);
    clearSelection();
  };

  const allSelected =
    filteredProducts.length > 0 &&
    selectedProducts.length === filteredProducts.length;

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <ProductsHeader onAddProduct={() => setShowAddModal(true)} />

          {/* Filters & Search Bar */}
          <ProductsFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            popularFilter={popularFilter}
            setPopularFilter={setPopularFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            categories={categories}
          />


          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedProducts.length}
            onBulkAction={onBulkAction}
          />
        </div>

        {/* Products Table */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : filteredProducts.length === 0 ? (
          <EmptyState onAddProduct={() => setShowAddModal(true)} />
        ) : (
          <ProductsTable
            products={filteredProducts}
            selectedProducts={selectedProducts}
            allSelected={allSelected}
            onSelectAll={(checked) =>
              handleSelectAll(checked, filteredProducts)
            }
            onSelectProduct={handleSelectProduct}
            onViewProduct={handleViewProduct}
            onTogglePopular={handleTogglePopular}
            onToggleStatus={handleToggleStatus}
            onDeleteProduct={handleDeleteProduct}
            onEditProduct={handleEditProduct}
            loadingStatusMap={loadingStatusMap}
            loadingPopularMap={loadingPopularMap}
          />
        )}

        {/* View Product Modal */}
        {showViewModal && (
          <ViewProductModal
            product={viewingProduct}
            onClose={() => setShowViewModal(false)}
            onEditProduct={(product) => {
              setShowViewModal(false);   // Close the view modal
              handleEditProduct(product); // Open the add/edit modal in edit mode
            }}
          />
        )}

        {/* Add/Edit Product Modal */}
        {showAddModal && (
          <AddEditProductModal
            isOpen={showAddModal}
            isEditMode={!!editingProduct}
            product={editingProduct}
            categories={categories}
            onClose={() => {
              setShowAddModal(false);
              setEditingProduct(null);
            }}
            onSave={handleSaveProduct}
            onAddCategory={(newCategory) =>
              setCategories((prev) => [...prev, newCategory])
            }
          />
        )}
      </div>
    </AdminLayout>
  );
}
